import { Color, type CubeTexture, type Scene } from "three";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import Logger from "@/lib/logger.ts";
import type { Nullable } from "@/types";
import Resources from "../utils/Resources.ts";
import SceneHelpers from "../utils/SceneHelpers.ts";

export default class Environment {
    #scene: Scene;

    readonly #environmentMap: Nullable<CubeTexture>;
    readonly #sceneHelpers: SceneHelpers;

    constructor(scene: Scene) {
        this.#scene = scene;

        this.#environmentMap = Resources.getInstance().environmentMap;
        this.#sceneHelpers = new SceneHelpers(scene);
    }

    init() {
        if (this.#environmentMap) {
            this.#initEnvironmentMap();
        } else {
            this.#initBackground();
        }
    }

    #initEnvironmentMap() {
        Logger.info("Creating environment");
        DiagnosticsStore.collectEventLog("info", "[Renderer] Creating environment map");

        this.#scene.environment = this.#environmentMap;
        this.#scene.background = this.#environmentMap;
        this.#scene.environmentIntensity = 1.0;
        this.#scene.backgroundBlurriness = 0.25;
        this.#scene.backgroundIntensity = 1.0;

        this.#sceneHelpers.traverseMeshesWithMaterials((child) => {
            child.material.envMap = this.#environmentMap;
            child.material.envMapIntensity = this.#scene.environmentIntensity;
        });

        Logger.success("Finished creating environment!");
        DiagnosticsStore.collectEventLog("success", "[Renderer] Created environment map");
    }

    #initBackground() {
        Logger.info("Initializing background");
        DiagnosticsStore.collectEventLog("info", "[Renderer] Initializing background");

        const values = {
            color: "#eeeced",
        };

        this.#scene.background = new Color(values.color);

        Logger.success("Finished creating background");
        DiagnosticsStore.collectEventLog("info", "[Renderer] background initialized");
    }
}
